/**
 * `/lehrer`: the teacher's home. Three sections:
 *
 * 1. Spiel starten: one small card per game (tile, facts, start button).
 *    Hidden reference modules sit in a collapsed "Test- und Referenzmodule".
 * 2. Meine Sets: saved sets grouped by format, with search, new, edit,
 *    duplicate, delete, start.
 * 3. Laufende Spiele: sessions in lobby or running, with open and end.
 *
 * `?start=<gameId>` opens the start dialog right away (used by the format page).
 *
 * On a phone (U19) the page is calmer, with every function still there:
 * the games as a compact list, one "Neues Set" that asks for the game,
 * and per set "Starten" plus a "…" menu (Bearbeiten, Duplizieren, Löschen).
 */
import type { PublicHealthResponse, GameInfo, SessionInfo } from '@schulspiele/protocol';

import { createLifetime, type Lifetime } from '@shared/lifetime';
import { api } from '@shared/api';
import { esc, qs } from '@shared/dom';
import { t } from '@shared/i18n/de';
import { navigate } from '@shared/router';
import { confirmDialog, openDialog } from '@shared/ui/dialog';
import { toast, toastError } from '@shared/ui/toast';
import { gameTile } from '../../games';
import { workLayout } from '../layout';
import { currentTeacher, formatDate, phasePill, renderLogin, type Teacher } from './common';
import { openExamples } from './examples-dialog';
import { openPasswordDialog } from './password-dialog';
import { openStartDialog } from './start-dialog';

interface SetSummary {
  id: string;
  gameId: string;
  title: string;
  updatedAt: number;
  orphaned: boolean;
  complete: boolean;
  openIssues: number;
}

export function renderTeacher(mount: HTMLElement, query: URLSearchParams): () => void {
  const life = createLifetime();
  void (async () => {
    const teacher = await currentTeacher();
    if (life.isDisposed()) return;
    if (teacher === null) {
      renderLogin(mount, () => {
        renderTeacher(mount, query);
      });
      return;
    }
    await renderDashboard(mount, teacher, life);
    const start = query.get('start');
    if (start !== null && !life.isDisposed()) {
      history.replaceState(null, '', '/lehrer');
      void openStartDialog(start);
    }
  })();
  return () => {
    life.dispose();
  };
}

async function renderDashboard(
  mount: HTMLElement,
  teacher: Teacher,
  life: Lifetime,
): Promise<void> {
  const main = workLayout(
    mount,
    `<div class="page-head">
       <h1>${t('teacher.hello', { name: teacher.displayName })}</h1>
       <span class="section__tools">
         ${teacher.isAdmin ? `<a class="btn btn--ghost btn--small" href="/lehrer/konten" data-link data-accounts>Konten</a>` : ''}
         <button class="btn btn--ghost btn--small" data-password type="button">${t('password.change')}</button>
         <button class="btn btn--ghost btn--small" data-logout type="button">${t('nav.logout')}</button>
       </span>
     </div>
     <section class="section" aria-labelledby="h-games">
       <div class="section__head">
         <h2 id="h-games">${t('teacher.games')}</h2>
         <span class="section__tools">
           <a class="btn btn--small" href="/lehrer/generator" data-link title="Ein Quiz aus der Fragenbibliothek: Thema, Klasse, Schwierigkeit">Quiz-Generator</a>
         </span>
       </div>
       <div data-games aria-busy="true"></div>
     </section>
     <section class="section" aria-labelledby="h-sessions">
       <div class="section__head"><h2 id="h-sessions">${t('teacher.sessions')}</h2></div>
       <div data-sessions aria-busy="true"></div>
     </section>
     <section class="section" aria-labelledby="h-library">
       <div class="section__head">
         <h2 id="h-library">${t('library.title')}</h2>
         <span class="section__tools">
           <a class="btn btn--small" href="/lehrer/fragen" data-link title="Fragen nach Stufe, Fach und Unterthema, für jedes Spiel">Fragenbibliothek</a>
           <a class="btn btn--ghost btn--small" href="/lehrer/medien" data-link title="${t('library.mediaHint')}">${t('library.media')}</a>
         </span>
       </div>
       <div data-library aria-busy="true"></div>
     </section>`,
    { teacherName: teacher.displayName },
  );

  // The demo login is the admin too, but a demo has no account list.
  const accountsLink = main.querySelector<HTMLElement>('[data-accounts]');
  if (accountsLink !== null) {
    void api<PublicHealthResponse>('/health')
      .then((h) => {
        if (h.demo) accountsLink.hidden = true;
      })
      .catch(() => undefined);
  }
  qs(main, '[data-password]').addEventListener('click', () => {
    void openPasswordDialog();
  });
  qs(main, '[data-logout]').addEventListener('click', () => {
    void api('/api/logout', { method: 'POST' }).then(() => {
      navigate('/');
    });
  });

  const [{ games }, { sets }, { sessions }] = await Promise.all([
    api<{ games: GameInfo[] }>('/api/games?all=1'),
    api<{ sets: SetSummary[] }>('/api/sets'),
    api<{ sessions: SessionInfo[] }>('/api/sessions'),
  ]);
  if (life.isDisposed()) return;

  renderGames(qs(main, '[data-games]'), games);
  renderSessions(qs(main, '[data-sessions]'), sessions);
  // A game running: its Steuerung first (on a phone the games list is long).
  if (sessions.some((s) => s.phase !== 'ended')) {
    const running = qs(main, '[aria-labelledby="h-sessions"]');
    main.querySelector('[aria-labelledby="h-games"]')?.before(running);
  }
  renderLibrary(qs(main, '[data-library]'), games, sets);
}

/* ------------------------------------------------------------------------ */

function gameCard(g: GameInfo): string {
  const art = gameTile(g.id);
  return `<article class="game-card">
    <div class="game-card__tile" aria-hidden="true">${art ?? `<div class="tile tile--plain" style="height:100%">${esc(g.name)}</div>`}</div>
    <div class="game-card__meta">
      <div>
        <strong>${esc(g.name)}</strong><br />
        <span class="muted" style="font-size:var(--text-sm)">${g.devicesOptional ? t('format.devicesOptional') : g.needsStudentDevices ? t('format.devices') : t('format.noDevices')}</span>
      </div>
      <button class="btn btn--small" data-start="${esc(g.id)}" type="button">${g.hasSets || g.playsSetsOf.length > 0 || g.bank !== null ? t('teacher.start') : t('teacher.startNow')}</button>
    </div>
  </article>`;
}

function renderGames(host: HTMLElement, games: GameInfo[]): void {
  host.removeAttribute('aria-busy');
  const visible = games.filter((g) => !g.hidden);
  const hidden = games.filter((g) => g.hidden);
  host.innerHTML = `
    ${visible.length === 0 ? `<div class="muted-box">${t('teacher.noGames')}</div>` : `<div class="game-picker">${visible.map(gameCard).join('')}</div>`}
    ${
      hidden.length === 0
        ? ''
        : `<details style="margin-top:var(--space-4)">
             <summary class="muted">${t('teacher.hiddenGames')}</summary>
             <div class="game-picker" style="margin-top:var(--space-3)">${hidden.map(gameCard).join('')}</div>
           </details>`
    }`;
  host.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-start]');
    if (b !== null) void openStartDialog(b.dataset.start ?? '');
  });
}

/* ------------------------------------------------------------------------ */

function renderSessions(host: HTMLElement, sessions: SessionInfo[]): void {
  host.removeAttribute('aria-busy');
  const open = sessions.filter((s) => s.phase !== 'ended');
  if (open.length === 0) {
    host.innerHTML = `<div class="muted-box">${t('teacher.noSessions')}</div>`;
    return;
  }
  host.innerHTML = open
    .map(
      (s) => `<div class="session-row" data-session="${esc(s.id)}">
        <span><strong>${esc(s.gameName)}</strong> · PIN ${esc(s.pin)} · ${phasePill(s.phase, s.gameOver)} · ${t('session.participants', { count: s.participantCount })}</span>
        <span class="actions">
          <a class="btn btn--small" href="/steuerung/${esc(s.id)}" data-link>${t('session.openControl')}</a>
          <a class="btn btn--small btn--ghost" href="/lehrer/session/${esc(s.id)}" data-link>${t('teacher.open')}</a>
          <button class="btn btn--small btn--danger" data-end="${esc(s.id)}" type="button">${t('session.end')}</button>
        </span>
      </div>`,
    )
    .join('');
  host.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-end]');
    if (b === null) return;
    const id = b.dataset.end ?? '';
    void confirmDialog(t('session.confirmEnd'), {
      confirmLabel: t('session.end'),
      danger: true,
    }).then((ok) => {
      if (!ok) return;
      api(`/api/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' })
        .then(() => {
          host.querySelector(`[data-session="${CSS.escape(id)}"]`)?.remove();
          if (host.querySelector('[data-session]') === null) {
            host.innerHTML = `<div class="muted-box">${t('teacher.noSessions')}</div>`;
          }
          toast(t('session.endedToast'), 'success');
        })
        .catch(toastError);
    });
  });
}

/* ------------------------------------------------------------------------ */

function renderLibrary(host: HTMLElement, games: GameInfo[], initialSets: SetSummary[]): void {
  host.removeAttribute('aria-busy');
  let sets = initialSets;
  const withSets = games.filter((g) => g.hasSets);
  const nameOf = new Map(games.map((g) => [g.id, g.name]));

  host.innerHTML = `
    <div class="library-tools">
      <label class="field">${t('library.search')}<input type="search" data-search placeholder="${t('library.searchPlaceholder')}" /></label>
      ${withSets.length === 0 ? '' : `<button class="btn btn--ghost library-new-one" type="button" data-new-set>${t('library.newOne')}</button>`}
      ${withSets
        .map(
          (g) =>
            `<span class="library-new">
               <a class="btn btn--ghost" href="/lehrer/sets/neu?spiel=${esc(g.id)}" data-link>${t('library.newFor', { game: g.name })}</a>
               ${g.hasExamples ? `<button class="btn btn--ghost btn--small" type="button" data-examples="${esc(g.id)}">${t('library.fromExample')}</button>` : ''}
             </span>`,
        )
        .join('')}
    </div>
    <div data-groups></div>`;

  const groupsHost = qs(host, '[data-groups]');
  const search = qs<HTMLInputElement>(host, '[data-search]');

  const draw = (): void => {
    const needle = search.value.trim().toLowerCase();
    const filtered = sets.filter((s) => needle === '' || s.title.toLowerCase().includes(needle));
    if (sets.length === 0) {
      groupsHost.innerHTML = `<div class="muted-box">${withSets.length === 0 ? t('library.emptyNoFormats') : t('library.empty')}</div>`;
      return;
    }
    if (filtered.length === 0) {
      groupsHost.innerHTML = `<div class="muted-box">${t('library.noMatch')}</div>`;
      return;
    }
    const byGame = new Map<string, SetSummary[]>();
    for (const s of filtered) byGame.set(s.gameId, [...(byGame.get(s.gameId) ?? []), s]);
    groupsHost.innerHTML = [...byGame.entries()]
      .map(([gameId, list]) => {
        const name = nameOf.get(gameId) ?? t('library.orphaned');
        return `<div class="library-group">
          <h3>${esc(name)} <span class="muted" style="font-size:var(--text-sm)">${String(list.length)}</span></h3>
          ${list.map(setRow).join('')}
        </div>`;
      })
      .join('');
  };

  search.addEventListener('input', draw);
  draw();

  host.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-examples]');
    if (b !== null) void openExamples(b.dataset.examples ?? '');
    if ((e.target as HTMLElement).closest('[data-new-set]') !== null) chooseNewSet(withSets);
  });

  groupsHost.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const startB = target.closest<HTMLElement>('[data-start-set]');
    const dupB = target.closest<HTMLElement>('[data-duplicate]');
    const delB = target.closest<HTMLElement>('[data-delete]');
    const moreB = target.closest<HTMLElement>('[data-set-menu]');
    if (moreB !== null) {
      const set = sets.find((s) => s.id === moreB.dataset.setMenu);
      if (set !== undefined) void setMenu(set, moreB);
    } else if (startB !== null) {
      const set = sets.find((s) => s.id === startB.dataset.startSet);
      if (set !== undefined) void openStartDialog(set.gameId, { setId: set.id });
    } else if (dupB !== null) {
      api<SetSummary>(`/api/sets/${encodeURIComponent(dupB.dataset.duplicate ?? '')}/duplicate`, {
        method: 'POST',
      })
        .then((copy) => {
          sets = [copy, ...sets];
          draw();
          toast(t('library.duplicated'), 'success');
        })
        .catch(toastError);
    } else if (delB !== null) {
      const set = sets.find((s) => s.id === delB.dataset.delete);
      if (set === undefined) return;
      void confirmDialog(t('library.confirmDelete', { title: set.title }), {
        confirmLabel: t('library.delete'),
        danger: true,
      }).then((ok) => {
        if (!ok) return;
        api(`/api/sets/${encodeURIComponent(set.id)}`, { method: 'DELETE' })
          .then(() => {
            sets = sets.filter((s) => s.id !== set.id);
            draw();
            toast(t('library.deleted'), 'success');
          })
          .catch(toastError);
      });
    }
  });
}

/** Phone: one "Neues Set" asks for the game (and offers the templates). */
function chooseNewSet(games: GameInfo[]): void {
  const dialog = openDialog({
    title: t('library.newOne'),
    body: `<ul class="new-set-list">${games
      .map(
        (g) => `<li>
          <button class="btn btn--ghost" type="button" data-new="${esc(g.id)}">${esc(g.name)}</button>
          ${g.hasExamples ? `<button class="btn btn--ghost btn--small" type="button" data-example="${esc(g.id)}">${t('library.fromExample')}</button>` : ''}
        </li>`,
      )
      .join('')}</ul>`,
    actions: [{ label: t('teacher.cancel'), value: 'cancel', kind: 'ghost' }],
  });
  dialog.element.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const make = target.closest<HTMLElement>('[data-new]');
    const example = target.closest<HTMLElement>('[data-example]');
    if (make !== null) {
      dialog.close();
      // After the dialog is gone: until then the page behind cannot be typed into.
      const to = `/lehrer/sets/neu?spiel=${encodeURIComponent(make.dataset.new ?? '')}`;
      void dialog.gone.then(() => {
        navigate(to);
      });
    } else if (example !== null) {
      dialog.close();
      void openExamples(example.dataset.example ?? '');
    }
  });
}

/** Phone: "…" next to "Starten" opens the rest; the row's own buttons do the work. */
async function setMenu(set: SetSummary, more: HTMLElement): Promise<void> {
  const row = more.closest<HTMLElement>('.set-row');
  const choice = await openDialog({
    title: set.title === '' ? t('library.untitled') : set.title,
    body: '',
    actions: [
      { label: t('teacher.cancel'), value: 'cancel', kind: 'ghost' },
      ...(set.orphaned
        ? []
        : [
            { label: t('library.edit'), value: 'edit', kind: 'ghost' as const },
            { label: t('library.duplicate'), value: 'duplicate', kind: 'ghost' as const },
          ]),
      { label: t('library.delete'), value: 'delete', kind: 'danger' },
    ],
  }).result;
  if (choice === 'edit') navigate(`/lehrer/sets/${encodeURIComponent(set.id)}`);
  else if (choice === 'duplicate') row?.querySelector<HTMLElement>('[data-duplicate]')?.click();
  else if (choice === 'delete') row?.querySelector<HTMLElement>('[data-delete]')?.click();
}

function setRow(s: SetSummary): string {
  return `<div class="set-row ${s.complete ? '' : 'set-row--draft'}">
    <div>
      <div class="set-row__title">${s.title === '' ? `<span class="muted">${t('library.untitled')}</span>` : esc(s.title)}
        ${s.complete ? '' : `<span class="draft-pill">${t('library.draft', { count: s.openIssues })}</span>`}</div>
      <div class="set-row__meta">${t('library.updated', { date: formatDate(s.updatedAt) })}</div>
    </div>
    <div class="set-row__actions">
      ${s.orphaned ? '' : `<button class="btn btn--small" type="button" data-start-set="${esc(s.id)}" ${s.complete ? '' : 'disabled title="Erst fertig machen, dann starten"'}>${t('teacher.start')}</button>`}
      ${s.orphaned ? '' : `<a class="btn btn--small btn--ghost set-row__extra" href="/lehrer/sets/${esc(s.id)}" data-link>${t('library.edit')}</a>`}
      ${s.orphaned ? '' : `<button class="btn btn--small btn--ghost set-row__extra" type="button" data-duplicate="${esc(s.id)}">${t('library.duplicate')}</button>`}
      <button class="btn btn--small btn--danger set-row__extra" type="button" data-delete="${esc(s.id)}">${t('library.delete')}</button>
      <button class="btn btn--small btn--ghost set-row__more" type="button" data-set-menu="${esc(s.id)}" aria-label="${esc(t('library.more', { title: s.title === '' ? t('library.untitled') : s.title }))}">…</button>
    </div>
  </div>`;
}
